<template>
  <div class="bp-card-container">
    <div class="bp-top-container">
      <div class="bp-header-container">
        <img
          src="@shared/assets/img/logo/black-short-logo.png"
          class="logo"
          alt="SmartAC.com"
        >
        <div class="bp-header">
          Smart Maintenance&reg; Plan Builder
        </div>
      </div>
      <button
        type="button"
        class="close-button"
        @click="toggleExitPopup(true)"
      >
        <img
          src="@shared/assets/img/general/x-black.png"
          class="close-img"
          alt="Close"
        >
      </button>
    </div>
    <div class="bp-body-container">
      <div class="bp-section-container">
        <div class="bp-section-body-container">
          <div
            v-if="partnerSettings.LOGO_URL"
            class="bp-logo-container"
            :style="{ backgroundColor: partnerSettings.PRIMARY_COLOR }"
          >
            <img
              v-if="partnerLogo"
              class="bp-logo"
              :src="partnerLogo"
              alt="Logo"
            >
          </div>
          <PlanType
            :index="0"
            :active="activeStepIdx === 0"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :apponlyAllowed="partnerSettings.APPONLY_ALLOWED"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
          />
          <NamePlan
            :index="1"
            :active="activeStepIdx === 1"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <PlanPricing
            :index="2"
            :active="activeStepIdx === 2"
            :changeActiveStepIdx="changeActiveStepIdx"
            :corporation="corporation"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <PlanDetails
            :index="3"
            :active="activeStepIdx === 3"
            :changeActiveStepIdx="changeActiveStepIdx"
            :corporation="corporation"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :toggleInfoModal="toggleInfoModal"
            :changeBuilderState="changeBuilderState"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <SignUpBonus
            :index="4"
            :active="activeStepIdx === 4"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
          <ExclusionDisclaimer
            :index="5"
            :active="activeStepIdx === 5"
            :changeActiveStepIdx="changeActiveStepIdx"
            :plan="plan"
            :updatePlan="updatePlan"
            :finishedSection="finishedSection"
            :partnerSettings="partnerSettings"
            :markUnfinishedSection="markUnfinishedSection"
            :markFinishedSection="markFinishedSection"
          />
        </div>
        <div class="bp-footer-container">
          <button
            type="button"
            class="bp-footer-button"
            :disabled="finishedSection.length !== 6"
            @click="createNewPlan(true)"
          >
            Save and Exit
          </button>
          <button
            type="button"
            class="bp-footer-button"
            :disabled="finishedSection.length !== 6"
            @click="createNewPlan(false)"
          >
            Save and Add New Plan
          </button>
        </div>
      </div>
      <div class="bp-plan-container">
        <div class="bp-body-header">
          *SmartAC.com recommends creating no more than 3 plans for customers
          <br>
          to choose from if you would like to use our
          <a
            href="/one-pager.pdf"
            target="_blank"
          >
            One Pager
          </a>
          asset
        </div>
        <div class="flex-row align-start just-center">
          <PlanCard
            :plan="plan"
            :finishedSection="finishedSection"
            :partnerSettings="partnerSettings"
            hideDetails
          />
          <ExclusionCard
            :plan="plan"
            :partnerSettings="partnerSettings"
          />
        </div>
      </div>
    </div>
    <InfoModal
      :showInfoModal="showInfoModal"
      :infoModalView="infoModalView"
      :plan="plan"
      :close="() => toggleInfoModal(false)"
    />
    <ConfirmExitPopup
      :show="showExitPopup"
      :close="() => toggleExitPopup(false)"
      :exit="close"
    />
    <ProcessOverlay :show="creatingPlan" />
  </div>
</template>

<script>
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

import NamePlan from './NamePlan.vue';
import PlanPricing from './PlanPricing.vue';
import PlanType from './PlanType.vue';
import PlanDetails from './PlanDetails.vue';
import SignUpBonus from './SignUpBonus.vue';
import ExclusionDisclaimer from './ExclusionDisclaimer.vue';
import PlanCard from './PlanCard.vue';
import ExclusionCard from './ExclusionCard.vue';
import InfoModal from './InfoModal.vue';
import ConfirmExitPopup from './ConfirmExitPopup.vue';

export default {
  name: 'PlanBuilder',
  components: {
    NamePlan,
    PlanPricing,
    PlanType,
    PlanDetails,
    SignUpBonus,
    ExclusionDisclaimer,
    PlanCard,
    ExclusionCard,
    InfoModal,
    ConfirmExitPopup,
    ProcessOverlay,
  },
  props: {
    close: Function,
    corporation: Object,
    partnerSettings: Object,
  },
  data() {
    return {
      activeStepIdx: 0,
      plan: {
        corpId: this.corporation.id,
        name: '',
        chargeFrequency: 'Monthly',
        priceInitial: null,
        priceSecond: null,
        priceAddon: null,
        creditAccrualMethod: null,
        comfortCreditEnabled: null,
        comfortCreditInitial: null,
        comfortCreditSecond: null,
        comfortCreditAddon: null,
        comfortCreditApplication: '',
        minRepairCost: null,
        redemptionRestriction: '',
        inspectionPerson: null,
        inspectionVirtual: null,
        inspectionFlex: null,
        bonusComfortCredit: null,
        bonusFilterVoucherFrequency: '',
        bonusFilterVoucher: null,
        voucherAccrualMethod: null,
        SignupBonusAccrual: null,
        services: [],
        status: 'Active',
        planType: 'Hardware',
      },
      finishedSection: ['planType'],
      creatingPlan: false,
      showInfoModal: false,
      infoModalView: '',
      showExitPopup: false,
      partnerLogo: null,
    };
  },
  mounted() {
    const {
      LOGO_URL,
    } = this.partnerSettings;
    this.$store.dispatch('getProviderServices', {
      corpId: this.corporation.id,
    });
    if (LOGO_URL) {
      this.loadImage(LOGO_URL);
    }
  },
  methods: {
    changeBuilderState(field, value) {
      if (this[field] !== undefined) {
        this[field] = value;
      }
    },
    changeActiveStepIdx(idx) {
      this.activeStepIdx = idx;
    },
    updatePlan(newPlan, finishedSection) {
      this.plan = newPlan;
      if (finishedSection === 'planType') {
        this.markUnfinishedSection('planDetail');
        this.plan = {
          ...this.plan,
          services: [],
        };
      }
      if (!this.finishedSection.includes(finishedSection)) {
        this.finishedSection = [
          ...this.finishedSection,
          finishedSection,
        ];
      }
    },
    markUnfinishedSection(section) {
      const newFinishedSection = this.finishedSection.slice();
      const sectionIdx = newFinishedSection.indexOf(section);
      if (sectionIdx >= 0) {
        newFinishedSection.splice(sectionIdx, 1);
      }
      this.finishedSection = newFinishedSection;
    },
    markFinishedSection(section) {
      if (!this.finishedSection.includes(section)) {
        this.finishedSection = [
          ...this.finishedSection,
          section,
        ];
      }
    },
    async createNewPlan(close) {
      this.creatingPlan = true;
      try {
        await this.$store.dispatch('createNewPlan', this.plan);
        this.creatingPlan = false;
        this.$store.dispatch('showSuccessToast', {
          message: `Success! ${this.plan.name} plan has been saved`,
        });
        if (close) {
          this.close();
        } else {
          this.activeStepIdx = 0;
          this.plan = {
            corpId: this.corporation.id,
            name: '',
            chargeFrequency: 'Monthly',
            priceInitial: null,
            priceSecond: null,
            priceAddon: null,
            creditAccrualMethod: null,
            comfortCreditEnabled: null,
            comfortCreditInitial: null,
            comfortCreditSecond: null,
            comfortCreditAddon: null,
            comfortCreditApplication: '',
            minRepairCost: null,
            redemptionRestriction: '',
            inspectionPerson: null,
            inspectionVirtual: null,
            inspectionFlex: null,
            bonusComfortCredit: null,
            bonusFilterVoucherFrequency: '',
            bonusFilterVoucher: null,
            voucherAccrualMethod: null,
            SignupBonusAccrual: null,
            services: [],
            status: 'Active',
            planType: 'Hardware',
          };
          this.finishedSection = ['planType'];
        }
      } catch (err) {
        this.$store.dispatch('showAlertToast', {
          message: 'There was an unexpected error updating plan. Please try again or contact support',
        });
        this.creatingPlan = false;
      }
    },
    toggleInfoModal(show, view) {
      if (show) {
        this.showInfoModal = show;
        this.infoModalView = view;
      } else {
        this.showInfoModal = show;
      }
    },
    toggleExitPopup(show) {
      this.showExitPopup = show;
    },
    async loadImage(imageUrl) {
      this.partnerLogo = await this.$store.dispatch('getImageResponse', {
        url: imageUrl,
        logo: true,
      });
    },
  },
};
</script>

<style lang="scss">

.bp-card-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: Poppins;

  @media (min-width: 1700px) {
    font-size: 120%;
  }

  .bp-top-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 63px;
    padding: 0 30px;
    background-color: #E0E8FF;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.08);

    .bp-header-container {
      display: flex;
      align-items: center;

      .logo {
        width: 35px;
        margin-right: 24px;
      }

      .bp-header {
        font-size: 1.25em;
        font-weight: 500;
        color: $NAVY100;
      }
    }

    .close-button {
      width: 28px;
      height: 28px;
      border-radius: 4px;
      background-color: $N5;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;

      .close-img {
        width: 10px;
        height: 10px;
      }
    }
  }

  .bp-body-container {
    flex: 1;
    width: 100%;
    height: 100%;
    display: flex;
    overflow-y: auto;

    .bp-section-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      height: 100%;
      overflow-y: auto;
      position: relative;

      .bp-section-body-container {
        padding: 24px 73px 100px 61px;
        flex: 1;

        .bp-logo-container {
          height: 80px;
          width: 80px;
          border-radius: 6px;
          padding: 4px;
          margin-bottom: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: auto;
          margin-right: auto;

          .bp-logo {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }
      }

      .bp-footer-container {
        position: sticky;
        bottom: 0;
        left: 0;
        right: 0;
        min-height: 74px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $WHITE;
        border-top: 1px solid $N5;

        .bp-footer-button {
          width: 165px;
          height: 24px;
          font-size: 0.625em;
          color: $WHITE;
          background-color: $NBLUE100;
          border-radius: 6px;
          white-space: nowrap;
          &[disabled] {
            background-color: $N40;
          }

          &:first-child {
            margin-right: 28px;
          }

          @media (min-width: 1700px) {
            width: 170px;
            height: 36px;
          }
        }
      }
    }

    .bp-plan-container {
      flex: 1;
      background-image: url('@shared/assets/img/setting/plan-popup-bg.png');
      background-size: 100% 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 66px;

      .bp-body-header {
        text-align: center;
        color: $N70;
        font-size: 0.875em;
        margin-bottom: 24px;

        a {
          font-weight: 500;
          color: $NBLUE100;
          text-decoration: underline;
        }
      }
    }

    .bp-disclaimer-container {
      background-color: $WHITE;
      border-radius: 14px;
      width: 350px;
      min-height: 400px;
      max-height: 700px;
      display: flex;
      flex-direction: column;
      .bp-disclaimer-header {
        height: 37px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 1px solid $N10;
        font-size: 1em;
        font-weight: 500;
        color: $N80;
      }

      .bp-disclaimer-body-container {
        flex: 1;
        padding: 10px 32px;
        overflow-y: auto;

        .bp-disclaimer-body-header {
          font-size: 0.875em;
          color: $N40;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .bp-disclaimer-body {
          font-size: 0.75em;
          color: $N40;
          margin-bottom: 8px;
        }

        .bp-disclaimer-body-title {
          margin-top: 16px;
          margin-bottom: 4px;
          color: $N80;
          font-weight: 500;
          font-size: 0.875em;
        }

        .bp-disclaimer-body-text {
          font-size: 0.75em;
          color: $N80;
        }
      }
    }
  }
}
</style>
